import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { map, Observable, shareReplay } from 'rxjs';
import { ApiResponse } from '../interfaces/common.interfaces';
import {
  DownloadDocumentsRequest,
  DownloadDocumentsResponse,
  DownloadEndorsementRequest,
  DownloadEndorsementResponse,
  DownloadRequest,
  DownloadData,
  RentCarListResponse,
  WorkshopListResponse,
} from '../interfaces/utilitarios.interface';
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { environment } from '../../environments/environment';

/**
 * Servicio para gestionar funcionalidades utilitarias como descargas de documentos y listados de talleres
 */
@Injectable({
  providedIn: 'root',
})
export class UtilitariosService {
  producerNumber: number | null = 0;
  private apiUrl = environment.int365_dom + '/api/v1';
  private apiUrl2 = environment.int365_dom + '/api/v2';
  private lastFetch: number = 0;
  private readonly CACHE_TIME = 30 * 60 * 1000; // 30 minutos en milisegundos
  private cachedWorkshops: Observable<
    ApiResponse<WorkshopListResponse[]>
  > | null = null;
  private lastWorkshopsFetch: number = 0;
  private cachedRentCars: Observable<ApiResponse<RentCarListResponse[]>> | null =
    null;
  private lastRentCarsFetch: number = 0;

  constructor(private authService: AuthService, private http: HttpClient) {}

  /**
   * Obtiene el listado de talleres afiliados
   * @param request Objeto con el número de productor
   * @returns Observable con la respuesta de la API conteniendo el listado de talleres
   */
  getWorkShop(request: {
    producerNumber: number;
  }): Observable<ApiResponse<WorkshopListResponse[]>> {
    const now = Date.now();
    if (
      !this.cachedWorkshops ||
      now - this.lastWorkshopsFetch > this.CACHE_TIME
    ) {
      this.lastWorkshopsFetch = now;
      this.cachedWorkshops = this.http
        .get<ApiResponse<WorkshopListResponse[]>>(
          `${this.apiUrl}/Dashboard/workshopList`,
          this.getHttpOptions(request)
        )
        .pipe(
          map((response) => {
            try {
              return response;
            } catch (error) {
              console.error('Error en la obtención de reclamos:', error);
              if (response.statusCode === 404) {
                throw { status: 404, error: response };
              }

              throw error;
            }
          }),
          shareReplay(1)
        );
    }
    return this.cachedWorkshops;
  }

  /**
   * Obtiene el listado de rent car afiliados
   * @param request Objeto con el número de productor
   * @returns Observable con la respuesta de la API conteniendo el listado de rent car
   */
  getRentCar(request: {
    producerNumber: number;
  }): Observable<ApiResponse<RentCarListResponse[]>> {
    const now = Date.now();
    if (
      !this.cachedRentCars ||
      now - this.lastRentCarsFetch > this.CACHE_TIME
    ) {
      this.lastRentCarsFetch = now;
      this.cachedRentCars = this.http
        .get<ApiResponse<RentCarListResponse[]>>(
          `${this.apiUrl}/Dashboard/rentcarList`,
          this.getHttpOptions(request)
        )
        .pipe(
          map((response) => {
            try {
              return response;
            } catch (error) {
              console.error('Error en la obtención de rent car:', error);
              if (response.statusCode === 404) {
                throw { status: 404, error: response };
              }

              throw error;
            }
          }),
          shareReplay(1)
        );
    }
    return this.cachedRentCars;
  }

  /**
   * Obtiene la producción para descargas de documentos
   * @param request Parámetros de búsqueda para la producción
   * @returns Observable con la respuesta de la API conteniendo los datos de producción
   */
  getProduction(
    request: DownloadRequest
  ): Observable<HttpResponse<ApiResponse<DownloadData>>> {
    return this.http.get<ApiResponse<DownloadData>>(
      `${this.apiUrl}/Production/production`,
      {
        ...this.getHttpOptions(request),
        observe: 'response',
        responseType: 'json',
      }
    );
  }

  /**
   * Obtiene documentos para imprimir (facturas y condiciones particulares)
   * @param request Parámetros para la solicitud de documentos
   * @returns Observable con la respuesta de la API conteniendo los documentos
   */
  getPrintDocuments(
    request: DownloadDocumentsRequest
  ): Observable<ApiResponse<DownloadDocumentsResponse>> {
    return this.http
      .get<ApiResponse<DownloadDocumentsResponse>>(
        `${this.apiUrl2}/documents`,
        this.getHttpOptions({
          ...request,
        })
      )
      .pipe(
        map((response) => {
          try {
            return response;
          } catch (error) {
            console.error('Error en la obtención de factura:', error);
            if (response.statusCode === 404) {
              throw { status: 404, error: response };
            }
            throw error;
          }
        })
      );
  }

  /**
   * Obtiene documentos de endoso
   * @param request Parámetros para la solicitud de endosos
   * @returns Observable con la respuesta de la API conteniendo los endosos
   */
  getEndorsement(
    request: DownloadEndorsementRequest
  ): Observable<ApiResponse<DownloadEndorsementResponse>> {
    return this.http
      .get<ApiResponse<DownloadEndorsementResponse>>(
        `${this.apiUrl}/Documents/endorsement`,
        this.getHttpOptions({
          ...request,
        })
      )
      .pipe(
        map((response) => {
          try {
            return response;
          } catch (error) {
            console.error('Error en la obtención de factura:', error);
            if (response.statusCode === 404) {
              throw { status: 404, error: response };
            }
            throw error;
          }
        })
      );
  }

  /**
   * Fuerza la actualización del caché de listas de talleres
   * @param request Objeto con el número de productor
   * @returns Observable con la respuesta actualizada de la API
   */
  forceListsRefresh(request: {
    producerNumber: number;
  }): Observable<ApiResponse<WorkshopListResponse[]>> {
    this.cachedWorkshops = null;
    this.lastWorkshopsFetch = 0;
    return this.getWorkShop(request);
  }

  /**
   * Configura las opciones HTTP incluyendo headers y parámetros
   * @param params Parámetros opcionales para la petición
   * @returns Objeto con las opciones HTTP configuradas
   */
  private getHttpOptions(params?: any) {
    const token = this.authService.getToken();
    if (!token) throw new Error('No token found');

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    return {
      headers,
      params: params
        ? new HttpParams({ fromObject: this.formatParams(params) })
        : undefined,
    };
  }

  /**
   * Formatea los parámetros para las peticiones HTTP
   * @param params Objeto con los parámetros a formatear
   * @returns Objeto con los parámetros formateados como strings
   */
  private formatParams(params: any): { [key: string]: string } {
    const formattedParams: { [key: string]: string } = {};

    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined) return;

      if (value instanceof Date) {
        formattedParams[key] = value.toISOString().split('T')[0];
      } else {
        formattedParams[key] = String(value);
      }
    });

    return formattedParams;
  }
}
